import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { Clock, ArrowLeft, Loader2 } from 'lucide-react';
import Seo from '../components/Seo';
import useReveal from '../hooks/useReveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = 'https://argecosmetics.co.uk';
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return ''; } };

const Article = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [state, setState] = useState('loading');
  useReveal(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    setState('loading');
    axios.get(`${API}/articles/${slug}`)
      .then((r) => { setPost(r.data); setState('ok'); })
      .catch(() => setState('error'));
    axios.get(`${API}/articles?limit=4`).then((r) => setRelated(r.data)).catch(() => {});
  }, [slug]);

  if (state === 'loading') return <div className="min-h-screen flex items-center justify-center pt-28"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;
  if (state === 'error' || !post) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 pt-28"><p className="font-serif text-3xl text-espresso">Article not found</p><Link to="/blog" className="text-gold underline">Back to the Journal</Link></div>
  );

  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'Article',
    headline: post.title, description: post.meta_description || post.excerpt,
    image: post.cover_image, datePublished: post.published_at,
    author: { '@type': 'Organization', name: 'ARGE Cosmetics' },
    publisher: { '@type': 'Organization', name: 'ARGE Cosmetics', logo: { '@type': 'ImageObject', url: `${SITE}/logo512.png` } },
    mainEntityOfPage: `${SITE}/blog/${post.slug}`,
  };

  return (
    <div>
      <Seo title={post.title} path={`/blog/${post.slug}`} type="article"
        description={post.meta_description || post.excerpt} image={post.cover_image} jsonLd={jsonLd} />

      <article className="pt-32 pb-10">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-mocha hover:text-gold transition-colors mb-8"><ArrowLeft className="w-4 h-4" /> The Journal</Link>
          <div className="flex flex-wrap items-center gap-3 text-[0.7rem] tracking-[0.18em] uppercase text-gold">
            <span>{fmt(post.published_at)}</span><span className="flex items-center gap-1 text-mocha"><Clock className="w-3 h-3" />{post.read_time} min read</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-espresso mt-4 leading-[1.1]">{post.title}</h1>
          {post.tags?.length ? (
            <div className="flex flex-wrap gap-2 mt-5">{post.tags.map((t) => <span key={t} className="text-[0.62rem] tracking-[0.12em] uppercase bg-cream text-espresso px-3 py-1">{t}</span>)}</div>
          ) : null}
        </div>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 mt-10">
          <div className="img-zoom aspect-[16/9] overflow-hidden shadow-[0_30px_70px_-30px_rgba(43,36,32,0.35)]"><img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" /></div>
        </div>
        <div className="max-w-3xl mx-auto px-6 lg:px-8 mt-12 article-body" dangerouslySetInnerHTML={{ __html: post.body_html }} />
      </article>

      {related.filter((r) => r.slug !== post.slug).length > 0 && (
        <section className="py-16 bg-cream">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <h2 className="font-serif text-3xl text-espresso mb-8">Continue Reading</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.filter((r) => r.slug !== post.slug).slice(0, 3).map((p) => (
                <Link to={`/blog/${p.slug}`} key={p.slug} className="group bg-ivory hover-lift">
                  <div className="img-zoom aspect-[16/10] overflow-hidden"><img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" /></div>
                  <div className="p-5"><p className="text-[0.6rem] tracking-[0.15em] uppercase text-gold">{fmt(p.published_at)}</p><h3 className="font-serif text-xl text-espresso mt-1 leading-snug group-hover:text-gold transition-colors line-clamp-2">{p.title}</h3></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Article;
